import { useEffect } from "react";
import { usePostHog } from "posthog-js/react";
import type { AbTestDefinition } from "libs/state/src/lib/ab-tests";
import {
  useSignupFlowStore,
} from "libs/state/src/lib/stores/flows/fresh/signupFlow/hooks/useSignUpFlowStore";

import { useFeatureFlagsContext } from "./featureFlagsContext";

interface UseFlagVariantOptions {
  formName?: string;
}

/**
 * Returns `true` when the current user is in the `"test"` variant for the
 * given AB test AND the current form is not excluded.
 *
 * For flow- and user-durable AB tests, the resolved variant is pinned to
 * the store on first evaluation so it persists throughout the session,
 * even if PostHog re-evaluates the flag.
 */
export function useFlagVariant(
  abTest: AbTestDefinition,
  { formName }: UseFlagVariantOptions = {},
): boolean {
  const { flags } = useFeatureFlagsContext();
  const { featureFlags: storedFlags, updateFeatureFlags } = useSignupFlowStore((state) => ({
    featureFlags: state.featureFlags,
    updateFeatureFlags: state.updateFeatureFlags,
  }));

  const posthog = usePostHog();
  const resolvedValue = flags[abTest.key];
  const shouldPinToStore = abTest.durability !== "none" && !(abTest.key in (storedFlags ?? {}));

  useEffect(() => {
    if (shouldPinToStore && resolvedValue !== undefined) {
      updateFeatureFlags({ [abTest.key]: resolvedValue });
    }
  }, [ shouldPinToStore, resolvedValue, abTest.key, updateFeatureFlags ]);

  // Calling getFeatureFlag() ensures we emit a $feature_flag_called event for analytics.
  // The SDK deduplicates per flag per session. The FeatureFlagsProvider already syncs
  // stored overrides into the SDK's internal state, so the logged variant matches.
  useEffect(() => {
    if (posthog && resolvedValue !== undefined) {
      posthog.getFeatureFlag(abTest.key);
    }
  }, [ posthog, abTest.key, resolvedValue ]);

  if (abTest.excludeForms?.has(formName ?? "")) {
    return false;
  }

  return resolvedValue === "test";
}
